import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, defer, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SpatialAssetRole, SpatialAvailability, SpatialBindingsRequest, SpatialEditor, SpatialFrontendConfig, SpatialInstallation, SpatialInstallationEditor, SpatialMutation, SpatialPortalMutation, SpatialProfileRequest, SpatialRegistrationEditor, SpatialScope, SpatialUnitAttestationRequest } from '../../shared/models/be/spatial';
import { RequestsService } from './utils/requests.service';
import { IUnit } from '../../shared/models/be/unit';

/** Resolves BE /v1 paths against the configured API, including the local dev proxy. */
export function spatialApiUrl(path: string): string {
  const api = new URL(environment.apiEndpoint, window.location.origin);
  const resource = new URL(path, api.origin);
  if (resource.origin !== api.origin || !resource.pathname.startsWith(api.pathname.replace(/\/$/, '') + '/')) {
    throw new Error('Risorsa Spatial fuori dal backend configurato.');
  }
  return environment.apiEndpoint.startsWith('/') ? resource.pathname + resource.search : resource.href;
}

@Injectable({ providedIn: 'root' })
export class SpatialService {
  private readonly http = inject(HttpClient);
  private readonly requests = inject(RequestsService);
  private authorized<T>(operation: () => Observable<T>): Observable<T> {
    return defer(() => this.requests.getCurrentUser()?.role === 'superuser'
      ? operation() : throwError(() => new Error('Spatial Tracking disponibile solo ai superuser.')));
  }
  private building(customerId: number, buildingId: string): string {
    return `${environment.apiEndpoint}/iot-devices/spatial/customers/${customerId}/buildings/${encodeURIComponent(buildingId)}`;
  }
  private unit(scope: SpatialScope): string {
    return `${this.building(scope.customerId, scope.buildingId)}/units/${encodeURIComponent(scope.unitId)}`;
  }
  availability(customerId: number, buildingId: string): Observable<SpatialAvailability> {
    return this.authorized(() => this.http.get<SpatialAvailability>(`${this.building(customerId, buildingId)}/availability`));
  }
  installations(scope: SpatialScope): Observable<{ installations: SpatialInstallation[] }> {
    return this.authorized(() => this.http.get<{ installations: SpatialInstallation[] }>(`${this.building(scope.customerId, scope.buildingId)}/installations`, { params: { unitId: scope.unitId } }));
  }
  units(scope: SpatialScope): Observable<{ units: IUnit[] }> {
    return this.authorized(() => this.http.get<{ units: IUnit[] }>(`${environment.apiEndpoint}/facilities/units`, { params: { buildingId: scope.buildingId } }));
  }
  private installation(scope: SpatialScope, id: string): string {
    return `${this.building(scope.customerId, scope.buildingId)}/installations/${encodeURIComponent(id)}`;
  }
  createInstallation(scope: SpatialScope, id: string, key: string): Observable<HttpResponse<SpatialPortalMutation>> {
    return this.authorized(() => this.http.post<SpatialPortalMutation>(`${this.building(scope.customerId, scope.buildingId)}/installations`, {
      installation_id: id
    }, { observe: 'response', headers: new HttpHeaders({ 'If-None-Match': '*', 'Idempotency-Key': key }) }));
  }
  installationEditor(scope: SpatialScope, id: string): Observable<HttpResponse<SpatialInstallationEditor>> {
    return this.authorized(() => this.http.get<SpatialInstallationEditor>(`${this.installation(scope, id)}/editor`, { observe: 'response' }));
  }
  adoptProfile(scope: SpatialScope, id: string, body: SpatialProfileRequest, etag: string, key: string): Observable<HttpResponse<SpatialPortalMutation>> {
    return this.authorized(() => this.http.put<SpatialPortalMutation>(`${this.installation(scope, id)}/profile`, body, { observe: 'response', headers: this.headers(etag, key) }));
  }
  attestProducer(scope: SpatialScope, id: string, reference: string, etag: string, key: string): Observable<HttpResponse<SpatialPortalMutation>> {
    return this.authorized(() => this.http.post<SpatialPortalMutation>(`${this.installation(scope, id)}/attestations`, { kind: 'PRODUCER', reference }, {
      observe: 'response', headers: this.headers(etag, key)
    }));
  }
  registration(scope: SpatialScope, draftId: string): Observable<HttpResponse<SpatialRegistrationEditor>> {
    return this.authorized(() => this.http.get<SpatialRegistrationEditor>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/registration`, { observe: 'response' }));
  }
  saveBindings(scope: SpatialScope, draftId: string, body: SpatialBindingsRequest, etag: string, key: string): Observable<HttpResponse<SpatialPortalMutation>> {
    return this.authorized(() => this.http.put<SpatialPortalMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/registration/bindings`, body, {
      observe: 'response', headers: this.headers(etag, key)
    }));
  }
  attestUnit(scope: SpatialScope, draftId: string, body: SpatialUnitAttestationRequest, etag: string, key: string): Observable<HttpResponse<SpatialPortalMutation>> {
    return this.authorized(() => this.http.post<SpatialPortalMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/registration/attestations`, body, {
      observe: 'response', headers: this.headers(etag, key)
    }));
  }
  editor(scope: SpatialScope): Observable<HttpResponse<SpatialEditor>> {
    return this.authorized(() => this.http.get<SpatialEditor>(`${this.unit(scope)}/editor`, { observe: 'response' }));
  }
  config(scope: SpatialScope): Observable<SpatialFrontendConfig> {
    return this.authorized(() => this.http.get<SpatialFrontendConfig>(`${this.unit(scope)}/config`));
  }
  image(path: string): Observable<Blob> {
    return this.authorized(() => this.http.get(spatialApiUrl(path), { responseType: 'blob' }));
  }
  createDraft(scope: SpatialScope, installationId: string, baseActiveRevisionId: string | null, etag: string, key: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.post<SpatialMutation>(`${this.unit(scope)}/drafts`, {
      installation_id: installationId, baseActiveRevisionId
    }, { observe: 'response', headers: this.headers(etag, key) }));
  }
  upload(scope: SpatialScope, draftId: string, role: SpatialAssetRole, file: File, etag: string): Observable<HttpEvent<SpatialMutation>> {
    const body = new FormData(); body.append('file', file, file.name);
    return this.authorized(() => this.http.put<SpatialMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/assets/${role}`, body, {
      observe: 'events', reportProgress: true, headers: this.headers(etag)
    }));
  }
  save(scope: SpatialScope, draftId: string, baseActiveRevisionId: string | null, etag: string, key: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.post<SpatialMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/save`, { baseActiveRevisionId }, {
      observe: 'response', headers: this.headers(etag, key)
    }));
  }
  removeAsset(scope: SpatialScope, draftId: string, role: SpatialAssetRole, etag: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.delete<SpatialMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}/assets/${role}`, { observe: 'response', headers: this.headers(etag) }));
  }
  discard(scope: SpatialScope, draftId: string, etag: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.delete<SpatialMutation>(`${this.unit(scope)}/drafts/${encodeURIComponent(draftId)}`, { observe: 'response', headers: this.headers(etag) }));
  }
  deactivate(scope: SpatialScope, revision: string | null, etag: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.post<SpatialMutation>(`${this.unit(scope)}/deactivate`, { expectedActiveRevisionId: revision }, { observe: 'response', headers: this.headers(etag) }));
  }
  removeConfiguration(scope: SpatialScope, revision: string | null, etag: string): Observable<HttpResponse<SpatialMutation>> {
    return this.authorized(() => this.http.delete<SpatialMutation>(`${this.unit(scope)}/configuration`, { body: { expectedActiveRevisionId: revision }, observe: 'response', headers: this.headers(etag) }));
  }
  private headers(etag: string, key?: string): HttpHeaders {
    let headers = new HttpHeaders({ 'If-Match': etag });
    if (key) headers = headers.set('Idempotency-Key', key);
    return headers;
  }
}
